// Insight: "klasifikasi data yang udah bagus yang mana, yang butuh perhatian
// yang mana". One generic page for every tenant, driven by
// GET /api/i/:slug/insights; the overview is read only to name each
// finding's stage, so a failed overview still leaves every finding readable.
//
// Section concepts (JEV ui.region_gate, 2026-10-12):
//   Ringkasan  job: see the count per verdict and narrow the page to one.      one row of three choice cards
//   Temuan     job: read each finding against its rule, check its chart, act.  per verdict a band of record rows,
//              the reading beside its chart (container rows, 0.79).
// Dropped by the gate: the verdict donut (implement 0.32, relevance 0.96), it repeated the counts.
import { useState, type ReactNode } from "react";
import { ChoiceCard, EmptyState, Notice, PageHeader, VerdictBadge, VizBand, VizList, VizRow } from "@dashboard-intelligent/ui";
import type { Insight, Verdict } from "@dashboard-intelligent/api/src/modules/industry/types";
import { endpoints } from "../endpoints";
import { VERDICT_TEXT } from "../format";
import { useIndustry } from "../industry";
import { useApi } from "../useApi";
import { Finding } from "./insight/Finding";
import { countByVerdict, findingPlan, VERDICT_ORDER } from "./insight/model";

/** What each verdict asks of a reader, said once, under its count. */
const VERDICT_MEANING: Record<Verdict, string> = {
  attention: "Kerjakan tindakannya lebih dulu.",
  watch: "Belum kritis. Pantau arahnya sebelum melewati batas.",
  good: "Sesuai atau melampaui aturannya. Pertahankan.",
};

export function InsightPage() {
  const { slug, summary } = useIndustry();
  const insights = useApi(() => endpoints.insights(slug));
  const overview = useApi(() => endpoints.overview(slug));
  const actions = useApi(() => endpoints.actions(slug));
  const name = summary?.shortName ?? slug;
  const header = (description: string) => <PageHeader title="Insight" description={description} />;

  if (insights.status === "loading") return header(`Memuat temuan ${name}.`);

  if (insights.status === "error") {
    return (
      <>
        {header(`Temuan ${name}.`)}
        <Notice
          tone="danger"
          title="Temuan gagal dimuat"
          action={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                insights.reload();
                overview.reload();
              }}
            >
              Coba lagi
            </button>
          }
        >
          <p>{insights.error}</p>
        </Notice>
      </>
    );
  }

  const stageLabels = new Map(overview.status === "ready" ? overview.data.stages.map((s) => [s.id, s.label] as const) : []);
  return (
    <InsightView
      name={name}
      insights={insights.data}
      stageLabel={(id) => stageLabels.get(id) ?? id}
      actionTitles={new Map(actions.status === "ready" ? actions.data.map((a) => [a.id, a.title] as const) : [])}
      header={header(`Mana yang berjalan baik dan mana yang perlu perhatian di ${name}.`)}
    />
  );
}

interface InsightViewProps {
  actionTitles: Map<string, string>;
  name: string;
  insights: Insight[];
  stageLabel: (stageId: string) => string;
  header: ReactNode;
}

function InsightView({ name, insights, stageLabel, header, actionTitles }: InsightViewProps) {
  const [filter, setFilter] = useState<Verdict | null>(null);

  if (insights.length === 0) {
    return (
      <>
        {header}
        <EmptyState icon="insight" title={`Belum ada temuan untuk ${name}`}>
          Temuan muncul setelah metrik industri ini didefinisikan dan dihitung dari datanya.
        </EmptyState>
      </>
    );
  }

  const counts = countByVerdict(insights);
  const shown = VERDICT_ORDER.filter((v) => counts[v] > 0 && (filter === null || filter === v));
  return (
    <>
      {header}
      <VizRow label={`Ringkasan temuan ${name}`}>
        {VERDICT_ORDER.map((verdict) => (
          <ChoiceCard
            key={verdict}
            span={4}
            value={String(counts[verdict])}
            label={<VerdictBadge verdict={verdict} />}
            description={VERDICT_MEANING[verdict]}
            pressed={filter === verdict}
            disabled={counts[verdict] === 0}
            ariaLabel={`${counts[verdict]} temuan ${VERDICT_TEXT[verdict].toLowerCase()}. ${filter === verdict ? "Tampilkan semua temuan" : "Tampilkan hanya temuan ini"}`}
            onPress={() => setFilter(filter === verdict ? null : verdict)}
          />
        ))}
      </VizRow>
      {shown.map((verdict) => {
        const list = insights.filter((i) => i.verdict === verdict);
        return (
          <VizBand key={verdict} title={VERDICT_TEXT[verdict]} meta={`${list.length} temuan`} id={`verdict-${verdict}`}>
            <VizList label={`Temuan ${VERDICT_TEXT[verdict].toLowerCase()}`}>
              {list.map((insight) => (
                <Finding key={insight.id} plan={findingPlan(insight)} stageLabel={stageLabel(insight.stageId)} actionTitles={actionTitles} />
              ))}
            </VizList>
          </VizBand>
        );
      })}
    </>
  );
}
